import * as DialogPrimitive from '@radix-ui/react-dialog'
import { XIcon } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useRef,
  type ComponentProps,
  type PointerEvent,
} from 'react'
import { cn } from '@/lib/cn'
import { esDelPicker } from '@/lib/drive/picker'

/**
 * Modal del sistema sobre Radix Dialog: focus-trap, Escape, scroll bloqueado
 * y accesibilidad resueltas (§2.8).
 *
 * Escritorio: tarjeta centrada de radio grande, con X arriba a la derecha.
 *
 * Celular: una **hoja** que sube desde abajo, con cuatro salidas fáciles
 * (acordado en la revisión de la Fase 0 con el Opportunity Owner):
 *   1. tocar la zona oscurecida, arriba de la hoja;
 *   2. deslizar la hoja hacia abajo desde la manija;
 *   3. el gesto o botón "atrás" del teléfono cierra el modal, no el sitio;
 *   4. el pie fijo con Cancelar a ancho completo, al alcance del pulgar.
 * La X no se muestra en celular: quedaba donde el dedo no llega.
 *
 * `tamano="normal"` (C8, confirmaciones): la hoja mide lo que su contenido.
 * `tamano="grande"` (C3, C5): hoja alta, ~94 % de la pantalla, con una
 * franja oscurecida arriba que también cierra. Es la "pantalla completa"
 * del §8.4 con una salida visible.
 */
export const Dialogo = DialogPrimitive.Root
export const DialogoDisparador = DialogPrimitive.Trigger
export const DialogoCerrar = DialogPrimitive.Close
export const DialogoTitulo = DialogPrimitive.Title
export const DialogoDescripcion = DialogPrimitive.Description

interface DialogoContenidoProps extends ComponentProps<typeof DialogPrimitive.Content> {
  /** `grande` para C3/C5 (dos columnas); `normal` para C8. */
  tamano?: 'normal' | 'grande'
  /** Oculta la X de escritorio cuando el contenido trae su propio encabezado. */
  sinCerrar?: boolean
}

const UMBRAL_DESLIZAMIENTO_PX = 80

export function DialogoContenido({
  className,
  children,
  tamano = 'normal',
  sinCerrar = false,
  ...props
}: DialogoContenidoProps) {
  const cerrarOculto = useRef<HTMLButtonElement>(null)
  // Estable entre renders: los hooks de los gestos dependen de él.
  const cerrar = useCallback(() => cerrarOculto.current?.click(), [])

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        data-fondo-modal
        className={cn(
          'fixed inset-0 z-40 bg-tinta/40',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        )}
      />
      <DialogPrimitive.Content
        data-tamano={tamano}
        onInteractOutside={(e) => {
          // Los menús desplegables y los selects se dibujan en su propio
          // portal, fuera del modal: tocarlos no es "tocar afuera".
          // Lo mismo el selector de Google Drive, que se abre encima.
          const destino = e.target
          if (
            (destino instanceof Element &&
              destino.closest('[data-radix-popper-content-wrapper]')) ||
            esDelPicker(destino)
          ) {
            e.preventDefault()
          }
          props.onInteractOutside?.(e)
        }}
        className={cn(
          'fixed z-50 flex flex-col bg-tarjeta text-tinta shadow-tarjeta outline-none',
          // Celular: hoja desde abajo. Con el teclado abierto se apoya encima de
          // él (--alto-teclado) y mide lo que queda a la vista (--alto-visible),
          // ambos de useCampoALaVista; sin teclado, la pantalla (OBS-009).
          'inset-x-0 bottom-[var(--alto-teclado,0px)] rounded-t-modal',
          'pb-[env(safe-area-inset-bottom)]',
          tamano === 'grande'
            ? 'h-[calc(var(--alto-visible,100dvh)*0.94)]'
            : 'max-h-[calc(var(--alto-visible,100dvh)*0.85)]',
          'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-full',
          'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom-full',
          // Escritorio: tarjeta centrada.
          'sm:inset-auto sm:top-1/2 sm:left-1/2 sm:h-auto sm:max-h-[calc(100dvh-3rem)]',
          'sm:w-[calc(100vw-3rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-modal sm:pb-0',
          'sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=open]:zoom-in-95',
          'sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=closed]:zoom-out-95',
          // En pantallas grandes el panel se abre más: la pieza crece con el alto.
          tamano === 'grande' ? 'sm:max-w-5xl 2xl:max-w-6xl' : 'sm:max-w-lg',
          className,
        )}
        {...props}
      >
        <Gestos cerrar={cerrar} />

        {children}

        {/* Botón invisible que dispara el cierre desde gestos y desde "atrás". */}
        <DialogPrimitive.Close
          ref={cerrarOculto}
          className="hidden"
          tabIndex={-1}
          aria-hidden
        />

        {!sinCerrar && (
          <DialogPrimitive.Close
            className={cn(
              'absolute top-3 right-3 hidden size-11 items-center justify-center rounded-full sm:inline-flex',
              'text-tinta-suave hover:bg-lienzo hover:text-tinta',
            )}
            aria-label="Cerrar"
          >
            <XIcon className="size-5" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

/**
 * Manija de la hoja (celular) y gesto "atrás". Vive DENTRO del contenido de
 * Radix, que sólo se monta con el modal abierto: así la entrada de historial
 * existe únicamente mientras el modal está en pantalla. Con los hooks en el
 * componente exterior, un modal cerrado pero montado (una confirmación)
 * empujaba una entrada al montarse y hacía `history.back()` al desmontarse,
 * lo que al cerrar sesión sacaba del sitio a quien usaba el celular.
 */
function Gestos({ cerrar }: { cerrar: () => void }) {
  useCerrarConAtras(cerrar)
  const manija = useDeslizarParaCerrar(cerrar)
  return (
    <div
      className="flex shrink-0 cursor-grab touch-none justify-center pt-2 pb-1 sm:hidden"
      aria-hidden
      {...manija}
    >
      <span className="h-1.5 w-10 rounded-full bg-borde" />
    </div>
  )
}

export function DialogoEncabezado({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex flex-col gap-1 px-6 pt-3 sm:pt-6 sm:pr-16', className)}
      {...props}
    />
  )
}

export function DialogoCuerpo({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('min-h-0 flex-1 overflow-y-auto px-6 py-5', className)}
      {...props}
    />
  )
}

/**
 * Pie con las acciones. En celular queda fijo abajo con los botones a ancho
 * completo (Cancelar a la izquierda, Guardar a la derecha), al alcance del
 * pulgar; en escritorio, alineados a la derecha (C8).
 */
export function DialogoPie({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'mt-auto grid shrink-0 grid-cols-2 gap-3 border-t border-borde px-4 py-3',
        '[&>*]:min-h-12 [&>*]:w-full',
        'sm:flex sm:items-center sm:justify-end sm:px-6 sm:py-4 sm:[&>*]:min-h-11 sm:[&>*]:w-auto',
        className,
      )}
      {...props}
    />
  )
}

/**
 * El gesto o botón "atrás" del teléfono cierra el modal en vez de irse del
 * sitio. Al abrir se agrega una entrada al historial; al cerrar por otra vía
 * se la retira, así "atrás" después del modal sigue funcionando normal.
 */
let retiroPendiente: ReturnType<typeof setTimeout> | null = null

/**
 * Pila de modales abiertos y ventana de "retiro en curso".
 *
 * Cada modal abierto agrega una entrada al historial para que el gesto
 * "atrás" lo cierre en vez de sacar del sitio. Al cerrarse por otra vía, esa
 * entrada se retira con `history.back()`, que dispara un `popstate` que no es
 * un "atrás" de la persona: durante esa ventana nadie se cierra. Sin esto,
 * cerrar la confirmación de Equipo RASTRO cerraba también el modal de abajo,
 * y en celular llegaba a sacar del sitio.
 */
const pila: symbol[] = []
let retiroHasta = 0
const MARGEN_RETIRO_MS = 300

function useCerrarConAtras(cerrar: () => void) {
  useEffect(() => {
    // Si el efecto se re-monta enseguida (StrictMode en desarrollo), el retiro
    // de la entrada que dejó el desmontaje anterior se cancela.
    if (retiroPendiente) {
      clearTimeout(retiroPendiente)
      retiroPendiente = null
    }

    const yo = Symbol('dialogo')
    pila.push(yo)
    window.history.pushState({ dialogo: true }, '')

    let cerradoPorAtras = false
    const alVolver = () => {
      // Ni durante un retiro programado ni si hay otro modal encima.
      if (Date.now() < retiroHasta) return
      if (pila[pila.length - 1] !== yo) return
      cerradoPorAtras = true
      cerrar()
    }
    window.addEventListener('popstate', alVolver)

    return () => {
      window.removeEventListener('popstate', alVolver)
      const posicion = pila.lastIndexOf(yo)
      if (posicion !== -1) pila.splice(posicion, 1)
      if (!cerradoPorAtras) {
        retiroPendiente = setTimeout(() => {
          retiroPendiente = null
          const marcado =
            (window.history.state as { dialogo?: boolean } | null)?.dialogo === true
          if (marcado) {
            retiroHasta = Date.now() + MARGEN_RETIRO_MS
            window.history.back()
          }
        }, 0)
      }
    }
  }, [cerrar])
}

/** Deslizar la manija hacia abajo más de 80 px cierra la hoja. */
function useDeslizarParaCerrar(cerrar: () => void) {
  const inicioY = useRef<number | null>(null)

  return {
    onPointerDown: (e: PointerEvent<HTMLDivElement>) => {
      inicioY.current = e.clientY
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    onPointerUp: (e: PointerEvent<HTMLDivElement>) => {
      if (
        inicioY.current !== null &&
        e.clientY - inicioY.current > UMBRAL_DESLIZAMIENTO_PX
      ) {
        cerrar()
      }
      inicioY.current = null
    },
    onPointerCancel: () => {
      inicioY.current = null
    },
  }
}
